import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { ChevronDown, ChevronRight, CreditCard, Users } from 'lucide-react'
import { api, apiFetchJson } from '@/lib/api-client'
import { formatDateTime } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { FilterBar, type FilterDef } from '@/components/data/filter-bar'
import { formatCurrency, formatPercent, percent } from '@roi365/shared'
import { Fragment, useState } from 'react'

interface LicenseUser {
  id: string
  display_name: string
  user_principal_name: string
  department: string | null
  account_enabled: number
  last_sign_in_at: string | null
}

const USERS_PAGE_SIZE = 50

/** Users holding one license, paginated, with a link to their full record in /users. */
function LicenseUsersPanel({ m365TenantId, licenseId, assignedUnits }: { m365TenantId: string; licenseId: string; assignedUnits: number }) {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['subscriptions', 'license-users', m365TenantId, licenseId, page],
    queryFn: () => apiFetchJson<{ ok: boolean; data: LicenseUser[]; meta: { total: number; totalPages: number } }>(
      `/m365-tenants/${m365TenantId}/users?licenseSkuId=${encodeURIComponent(licenseId)}&page=${page}&perPage=${USERS_PAGE_SIZE}`,
    ),
  })
  const users = data?.data ?? []
  const total = data?.meta.total ?? 0
  const totalPages = data?.meta.totalPages ?? 1

  return (
    <div className="border-t p-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-gray-50)' }}>
      {isLoading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-8 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />)}</div>
      ) : isError ? (
        <p className="text-sm" style={{ color: 'var(--color-error)' }}>No se pudieron cargar los usuarios de esta licencia.</p>
      ) : users.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>Ningún usuario tiene asignada esta licencia.</p>
      ) : (
        <>
          <p className="mb-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {total} usuario(s) con la licencia{total !== assignedUnits ? ` · ${assignedUnits} unidades asignadas según Microsoft` : ''}
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr>
                {['Usuario', 'Departamento', 'Estado', 'Último acceso'].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <td className="px-3 py-2">
                    <button type="button" className="text-left hover:underline" style={{ color: 'var(--color-text-link)' }}
                      onClick={() => navigate({ to: '/users', search: { email: u.user_principal_name, tenant: m365TenantId } })}>
                      <span className="font-medium">{u.display_name}</span>
                      <span className="block text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{u.user_principal_name}</span>
                    </button>
                  </td>
                  <td className="px-3 py-2" style={{ color: 'var(--color-text-secondary)' }}>{u.department ?? '—'}</td>
                  <td className="px-3 py-2">
                    <span className="text-xs" style={{ color: u.account_enabled ? 'var(--color-success-dark)' : 'var(--color-text-tertiary)' }}>{u.account_enabled ? 'Activo' : 'Deshabilitado'}</span>
                  </td>
                  <td className="px-3 py-2 tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>{u.last_sign_in_at ? formatDateTime(u.last_sign_in_at) : 'Nunca'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="mt-3 flex items-center justify-end gap-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
              <span>Página {page} de {totalPages}</span>
              <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export function SubscriptionsPage() {
  // Deep link (/subscriptions?license=<id>) from a finding: filter to that license and open its users.
  const licenseFromUrl = new URLSearchParams(window.location.search).get('license')
  const [filterValues, setFilterValues] = useState<Record<string, string>>(() => { const init: Record<string, string> = {}; if (licenseFromUrl) init.license = licenseFromUrl; return init })
  const [expandedId, setExpandedId] = useState<string | null>(licenseFromUrl)

  const { data: tenants } = useQuery({
    queryKey: ['m365-tenants'],
    queryFn: () => api.get<Array<{ id: string; display_name: string }>>('/m365-tenants'),
  })

  const { data: allSubscriptions, isLoading } = useQuery({
    queryKey: ['subscriptions', 'all', tenants?.map((t) => t.id)],
    queryFn: async () => {
      if (!tenants?.length) return []
      const results = await Promise.all(
        tenants.map(async (t) => {
          const subscriptions = await api.get<
            Array<{
              id: string; sku_part_number: string; display_name: string
              total_units: number; assigned_units: number; available_units: number
              monthly_cost_per_unit: number | null
            }>
          >(`/m365-tenants/${t.id}/licenses`)
          return subscriptions.map((s) => ({ ...s, tenant_name: t.display_name, m365_tenant_id: t.id }))
        }),
      )
      return results.flat()
    },
    enabled: !!tenants?.length,
  })

  const multiTenant = (tenants?.length ?? 0) > 1
  const filters: FilterDef[] = [
    { key: 'search', label: 'Suscripcion', type: 'search', placeholder: 'Buscar por nombre o SKU...' },
    {
      key: 'license', label: 'Licencia', type: 'select',
      options: (allSubscriptions ?? []).map((s) => ({ value: s.id, label: multiTenant ? `${s.display_name} · ${s.tenant_name}` : s.display_name })),
    },
  ]

  // Filter
  let filtered = allSubscriptions ?? []
  if (filterValues.license) filtered = filtered.filter((l) => l.id === filterValues.license)
  if (filterValues.search) {
    const s = filterValues.search.toLowerCase()
    filtered = filtered.filter(
      (l) => l.display_name.toLowerCase().includes(s) || l.sku_part_number.toLowerCase().includes(s),
    )
  }

  const totalCostAll = filtered.reduce((acc, l) => acc + (l.monthly_cost_per_unit ?? 0) * l.total_units, 0)
  const totalPurchased = filtered.reduce((acc, l) => acc + l.total_units, 0)
  const totalAssigned = filtered.reduce((acc, l) => acc + l.assigned_units, 0)

  return (
    <div className="space-y-6">
      <PageHeader title="Suscripciones" description="Inventario consolidado de suscripciones Microsoft 365" />

      {filtered.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <Card>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Coste mensual total</p>
            <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
              {formatCurrency(totalCostAll)}
            </p>
          </Card>
          <Card>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Suscripciones</p>
            <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
              {filtered.length}
            </p>
          </Card>
          <Card>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Unidades compradas</p>
            <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
              {totalPurchased.toLocaleString('es-ES')}
            </p>
          </Card>
          <Card>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Utilizacion</p>
            <p className="mt-1 text-2xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
              {formatPercent(percent(totalAssigned, totalPurchased))}
            </p>
          </Card>
        </div>
      )}

      <FilterBar
        filters={filters}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        onClear={() => setFilterValues({})}
      />

      <Card padding={false}>
        {isLoading ? (
          <div className="p-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="mb-3 h-12 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />
            ))}
          </div>
        ) : !filtered.length ? (
          <div className="p-6">
            <EmptyState icon={CreditCard} title="Sin suscripciones" description="Conecta un tenant M365 para ver el inventario de suscripciones" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: 'var(--color-gray-50)' }}>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--color-text-secondary)' }}>Nombre</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--color-text-secondary)' }}>Unidades</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--color-text-secondary)' }}>Uso</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--color-text-secondary)' }}>Precio/ud/mes</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--color-text-secondary)' }}>Usuarios</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((sub) => {
                  const pct = percent(sub.assigned_units, sub.total_units)
                  const isExpanded = expandedId === sub.id
                  return (
                    <Fragment key={sub.id}>
                    <tr className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                      <td className="px-4 py-3">
                        <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                          {sub.display_name}
                        </p>
                        {multiTenant && <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{sub.tenant_name}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                          {sub.assigned_units}
                        </span>
                        <span style={{ color: 'var(--color-text-tertiary)' }}> / {sub.total_units}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-20 rounded-full" style={{ backgroundColor: 'var(--color-gray-200)' }}>
                            <div
                              className="h-2 rounded-full"
                              style={{
                                width: `${Math.min(pct, 100)}%`,
                                backgroundColor: pct > 90 ? 'var(--color-success)' : pct < 30 ? 'var(--color-warning)' : 'var(--color-brand-secondary)',
                              }}
                            />
                          </div>
                          <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-tertiary)' }}>
                            {formatPercent(pct)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="tabular-nums font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          {sub.monthly_cost_per_unit != null && sub.monthly_cost_per_unit > 0
                            ? formatCurrency(sub.monthly_cost_per_unit)
                            : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="secondary" size="sm" onClick={() => setExpandedId(isExpanded ? null : sub.id)}>
                          {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          <Users className="h-3.5 w-3.5" /> {isExpanded ? 'Ocultar' : 'Ver usuarios'}
                        </Button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={5} className="p-0">
                          <LicenseUsersPanel m365TenantId={sub.m365_tenant_id} licenseId={sub.id} assignedUnits={sub.assigned_units} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
